import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { postListFetcher } from '@/shared/api/list';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig, ListRow } from '@/shared/ui/list/types';
import { EMPTY_NOTIFICATION, fetchNotificationCategories } from './api';
import { COUNTRY_OPTIONS, NOTIFICATION_TYPE_OPTIONS, optionLabel } from './constants';
import { NotificationEditModal } from './NotificationEditModal';
import { NotificationModal } from './NotificationModal';

interface Notification extends ListRow {
  id: number;
  type_id?: number;
  category_id?: number;
  message?: string;
  send_time?: number;
  is_sent?: number | boolean;
  count_recipients?: number;
  push_mobile_platform?: string;
  country_region_ids?: Array<{ country_id?: number }>;
}

type Dialog = { kind: 'create' } | { kind: 'edit'; item: Notification } | null;

export const NotificationsPage = () => {
  const [dialog, setDialog] = useState<Dialog>(null);
  const categories = useQuery({
    queryKey: ['notification-categories'],
    queryFn: fetchNotificationCategories,
  });

  const config = useMemo<ListPageConfig<Notification>>(() => {
    const categoryNames = Object.fromEntries(
      (categories.data ?? []).map(({ id, description }) => [id, description]),
    );

    return {
      queryKey: 'notifications',
      title: 'Уведомления',
      headerActions: (
        <button type="button" className="btn btn-info pull-right" onClick={() => setDialog({ kind: 'create' })}>
          Создать уведомление
        </button>
      ),
      errorText: 'Ошибка загрузки уведомлений',
      fetchList: postListFetcher('/notification/list/'),
      rows: ['notifications'],
      columns: [
        { header: 'id', render: (item) => item.id },
        { header: 'Тип уведомления', render: (item) => optionLabel(NOTIFICATION_TYPE_OPTIONS, item.type_id) },
        { header: 'Категория уведомления', render: (item) => categoryNames[Number(item.category_id)] },
        { header: 'Текст уведомления', render: (item) => item.message },
        { header: 'Количество адресатов', render: (item) => item.count_recipients },
        {
          header: 'Платформа',
          render: (item) =>
            item.push_mobile_platform === 'all' ? 'iOS, Android' : item.push_mobile_platform,
        },
        {
          header: 'География',
          render: (item) => optionLabel(COUNTRY_OPTIONS, item.country_region_ids?.[0]?.country_id),
        },
        { header: 'Время отправки', render: (item) => formatUnixDateTime(item.send_time) },
        { header: 'Статус отправки', render: (item) => (item.is_sent ? 'отправлено' : 'не отправлено') },
        {
          header: 'Обновить',
          render: (item) =>
            !item.is_sent && (
              <button type="button" className="btn btn-success" onClick={() => setDialog({ kind: 'edit', item })}>
                Обновить
              </button>
            ),
        },
      ],
    };
  }, [categories.data]);

  const categoryOptions = (categories.data ?? []).map(({ id, description, parent_id }) => ({
    value: String(id),
    label: parent_id ? `— ${description}` : description,
  }));
  const close = () => setDialog(null);

  return (
    <>
      <GenericListPage config={config} />
      {dialog?.kind === 'create' && (
        <NotificationModal initial={EMPTY_NOTIFICATION} categories={categoryOptions} onClose={close} />
      )}
      {dialog?.kind === 'edit' && (
        <NotificationEditModal
          id={dialog.item.id}
          row={dialog.item}
          categories={categoryOptions}
          onClose={close}
        />
      )}
    </>
  );
};
